import React, { useCallback } from 'react';
import includes from 'lodash/includes';

import { DateType } from '../../../../../../../types';

import {
  UniversalFilterDateModes,
  UniversalFilterDateOptions,
  UniversalFilterDateFilterState,
  UniversalFilterDateOnFilterStateChange
} from '../../UniversalFilterDate.types';
import { UniversalFilterDateIsAfterModeOptions } from './UniversalFilterDateIsAfterMode.types';

import {
  UniversalFilterOptionSelect,
  UniversalFilterOptionSelectOption,
  UniversalFilterOptionSelectOnChange
} from '../../../../helpers/UniversalFilterOptionSelect';

import { UniversalFilterPositiveIntegerInput } from '../../../../helpers/UniversalFilterPositiveIntegerInput';
import { UniversalFilterDateInput } from '../../../../helpers/UniversalFilterDateInput';

import { filtersKeys } from '../../../../../../../locales/keys';

const options: UniversalFilterOptionSelectOption<UniversalFilterDateIsAfterModeOptions>[] =
  [
    {
      value: UniversalFilterDateIsAfterModeOptions.EXACT_DAY,
      i18nText: filtersKeys.dateOptions.exactDay
    },
    {
      value: UniversalFilterDateIsAfterModeOptions.TODAY,
      i18nText: filtersKeys.dateOptions.today
    },
    {
      value: UniversalFilterDateIsAfterModeOptions.TOMORROW,
      i18nText: filtersKeys.dateOptions.tomorrow
    },
    {
      value: UniversalFilterDateIsAfterModeOptions.YESTERDAY,
      i18nText: filtersKeys.dateOptions.yesterday
    },
    {
      value: UniversalFilterDateIsAfterModeOptions.ONE_WEEK_AGO,
      i18nText: filtersKeys.dateOptions.oneWeekAgo
    },
    {
      value: UniversalFilterDateIsAfterModeOptions.ONE_WEEK_FROM_NOW,
      i18nText: filtersKeys.dateOptions.oneWeekFromNow
    },
    {
      value: UniversalFilterDateIsAfterModeOptions.ONE_MONTH_AGO,
      i18nText: filtersKeys.dateOptions.oneMonthAgo
    },
    {
      value: UniversalFilterDateIsAfterModeOptions.ONE_MONTH_FROM_NOW,
      i18nText: filtersKeys.dateOptions.oneMonthFromNow
    },
    {
      value: UniversalFilterDateIsAfterModeOptions.NUMBER_OF_DAYS_AGO,
      i18nText: filtersKeys.dateOptions.numberOfDaysAgo
    },
    {
      value: UniversalFilterDateIsAfterModeOptions.NUMBER_OF_DAYS_FROM_NOW,
      i18nText: filtersKeys.dateOptions.numberOfDaysFromNow
    }
  ];

const currentMode = UniversalFilterDateModes.IS_AFTER;

interface UniversalFilterDateIsAfterModeProps {
  filterState: UniversalFilterDateFilterState;
  autoFocus?: boolean;
  onFilterStateChange: UniversalFilterDateOnFilterStateChange;
}

function UniversalFilterDateIsAfterMode({
  filterState,
  autoFocus,
  onFilterStateChange
}: UniversalFilterDateIsAfterModeProps) {
  const handleChange = useCallback<
    UniversalFilterOptionSelectOnChange<UniversalFilterDateIsAfterModeOptions>
  >(
    (value) => {
      onFilterStateChange({ mode: currentMode, option: value });
    },
    [onFilterStateChange]
  );

  const handleDaysChange = useCallback<(value?: number) => void>(
    (value) => {
      onFilterStateChange({ ...filterState, optionDays: value });
    },
    [filterState, onFilterStateChange]
  );

  const handleDateChange = useCallback<(value?: DateType) => void>(
    (value) => {
      onFilterStateChange({ ...filterState, optionDate: value });
    },
    [filterState, onFilterStateChange]
  );

  const withDaysInput = includes(
    [
      UniversalFilterDateIsAfterModeOptions.NUMBER_OF_DAYS_AGO,
      UniversalFilterDateIsAfterModeOptions.NUMBER_OF_DAYS_FROM_NOW
    ],
    filterState.option
  );

  return (
    <>
      <div className="flex-1">
        <UniversalFilterOptionSelect<UniversalFilterDateOptions>
          options={options}
          selectedValue={filterState.option}
          onChange={handleChange}
        />
      </div>

      {filterState.option ===
      UniversalFilterDateIsAfterModeOptions.EXACT_DAY ? (
        <UniversalFilterDateInput
          key={[filterState.mode, filterState.option].join('-')}
          value={filterState.optionDate}
          autoFocus={withDaysInput ? false : autoFocus}
          onChange={handleDateChange}
        />
      ) : null}

      {withDaysInput ? (
        <UniversalFilterPositiveIntegerInput
          key={[filterState.mode, filterState.option].join('-')}
          value={filterState.optionDays}
          i18nPlaceholder={filtersKeys.enterDays}
          autoFocus={autoFocus}
          onChange={handleDaysChange}
        />
      ) : null}
    </>
  );
}

export default UniversalFilterDateIsAfterMode;
